// @flow
import types from "./types";
import type {Dispatch, Store} from "redux";
import type {StopType} from "../../../Utils/AppUtils";
import type {GetState} from "../../Store";
import {registerMiddleman} from "../Middleman/middleware";
import {StopEnum} from "../../../Utils/AppUtils";
import {strings} from "../../../config";
import ServiceConfigOperations from "../../Ducks/ServiceConfig/operations";
import CurrentRideSelectors from "../../Ducks/CurrentRide/selectors";
import CurrentRideOperations from "../../Ducks/CurrentRide/operations";
import AppSelectors from "../../Ducks/App/selectors";
import AppOperations from "../../Ducks/App/operations";
import CurrentRequestSelectors from "../../Ducks/CurrentRequest/selectors";
import CurrentProposalSelectors from "../../Ducks/CurrentProposal/selectors";
import CurrentProposalOperations from "../../Ducks/CurrentProposal/operations";
import actions from "./actions";
import CurrentRequestOperations from "../../Ducks/CurrentRequest/operations";
import MapDataOperations from "../../Ducks/MapData/operations";
import TokenDataOperations from "../../Ducks/TokenData/operations";


const thunkBackendAPICall = actions.PostAPI;

const doDecryptToken = (token: string) => thunkBackendAPICall({
    url: "/v1/token/decrypt",
    body: {token: token},
    success: [TokenDataOperations.updateRequestData, doGetServiceConfigurations, doStartupLastRideStatus],
    showLoading: true,
    error: [TokenDataOperations.failTokenDecrypt]
});


const doGetServiceConfigurations = () => thunkBackendAPICall({
    url: "v1/service/configurations",
    success: [ServiceConfigOperations.updateServiceConfigurationsData, ServiceConfigOperations.setServicePolygon,
        TokenDataOperations.getOriginTerminalsIfForced, TokenDataOperations.getDestTerminalsIfForced],
    error: [AppOperations.technicalIssue],
});

const doLastRideStatus = () => thunkBackendAPICall({
    url: "v1/last/ride/status",
    success: [CurrentRideOperations.updateRideDataSuccess],
    error: [AppOperations.failSilently],
});
registerMiddleman(types.MIDDLEMAN_RIDE_STATUS, doLastRideStatus);

const doStartupLastRideStatus = () => thunkBackendAPICall({
    url: "v1/last/ride/status",
    success: [CurrentRideOperations.updateStartupRideDataSuccess],
    error: [AppOperations.failSilently],
});


const doProposalStatus = () => {
    return (dispatch: Dispatch, getState: () => Store) => {
        dispatch(CurrentProposalOperations.acceptProposal());
        const state = getState();
        const proposalID = CurrentProposalSelectors.getProposalID(state);
        return dispatch(thunkBackendAPICall({
            url: "v1/proposal/status",
            success: [CurrentRideOperations.proposalStatusSuccess],
            body: {proposal_id: proposalID},
            error: [AppOperations.failSilently],
        }));
    };
};

const doCancelProposal = () => {
    return (dispatch: Dispatch, getState: () => Store) => {
        const state = getState();
        const proposalID = CurrentProposalSelectors.getProposalID(state);
        return dispatch(thunkBackendAPICall({
            url: "v1/proposal/cancel",
            success: [CurrentRequestOperations.invalidateRequestDestOrOrigin, CurrentProposalOperations.resetCurrentProposal],
            body: {proposal_id: proposalID},
            error: [(error) => dispatch(AppOperations.showAlert({message: error.message}))],
        }));
    };
};
registerMiddleman(types.MIDDLEMAN_CANCEL_PROPOSAL, doCancelProposal);

const doGetPolygonStops = (airportPolygonID: number, stopTypeEnum: StopType) => {
    const success = stopTypeEnum === StopEnum.PICKUP ?
        [ServiceConfigOperations.setOriginTerminalOptions] :
        [ServiceConfigOperations.setDestTerminalOptions];
    return thunkBackendAPICall({
        url: "v1/service/polygon/stops",
        body: {
            polygon_id: airportPolygonID,
            stop_type: stopTypeEnum,
        },
        success: success,
        error: [AppOperations.showAlert],
    });
};

const doGetPolygonPickupStops = (airportPolygonID: number) => doGetPolygonStops(airportPolygonID, StopEnum.PICKUP);
const doGetPolygonDropoffStops = (airportPolygonID: number) => doGetPolygonStops(airportPolygonID, StopEnum.DROPOFF);

const doRideRequest = () => {
    return (dispatch: Dispatch, getState: GetState) => {
        dispatch(MapDataOperations.focusMapBoundsOriginDest());
        const state = getState();
        const origin = CurrentRequestSelectors.getOrigin(state);
        const dest = CurrentRequestSelectors.getDest(state);
        const passengerCount = CurrentRequestSelectors.getPassengerCount(state);
        return dispatch(thunkBackendAPICall({
            url: "v1/ride/request",
            body: {
                passenger_count: parseInt(passengerCount, 10),
                origin: {...origin, description: origin.address},
                destination: {...dest, description: dest.address},
                support_driver_approval: true
            },
            showLoading: true,

            success: [CurrentProposalOperations.updateProposalData, MapDataOperations.focusMapBoundsOriginPickup],
            error: [CurrentProposalOperations.failProposalRequest],
        }));
    };
};
registerMiddleman(types.MIDDLEMAN_RIDE_REQUEST, doRideRequest);

const doAcceptRideProposal = () => {
    return (dispatch: Dispatch, getState: () => Store) => {
        dispatch(CurrentProposalOperations.acceptProposal());
        const state = getState();
        const proposalID = CurrentProposalSelectors.getProposalID(state);
        return dispatch(thunkBackendAPICall({
            url: "v1/ride/accept",
            body: {
                proposal_id: proposalID,
            },
            showLoading: true,

            success: [CurrentProposalOperations.handleAcceptedProposalSuccess],
            error: [CurrentProposalOperations.failProposalAccept],
        }));
    };
};

const doGetWalkingMap = () => {
    return (dispatch: Dispatch, getState: () => Store) => {
        dispatch(CurrentRideOperations.requestedWalkingMap());
        const state = getState();
        const riderLocation = AppSelectors.getRiderLocation(state);
        const pickup = CurrentRideSelectors.getPickup(state);
        return dispatch(thunkBackendAPICall({
            url: "v1/ride/walking/map",
            body: {
                riderLatLng: riderLocation,
                pickupLatLng: pickup,
            },

            success: [CurrentRideOperations.handleWalkingMapRequestSuccess],
            // Fail silently
        }));
    };
};

const doCancelRide = () => {
    return (dispatch: Dispatch, getState: GetState) => {
        return dispatch(thunkBackendAPICall({
            url: "v1/ride/cancel",
            body: {},
            loading: true,
            success: [CurrentRideOperations.rideDataClear],
            error: [(error) => dispatch(AppOperations.showAlert({message: error.message}))],
        }));
    };
};
registerMiddleman(types.MIDDLEMAN_RIDE_CANCEL, doCancelRide);

const doGetCancellationFee = () => {
    return (dispatch: Dispatch, getState: GetState) => {
        return dispatch(thunkBackendAPICall({
            url: "v1/ride/cancellation/details",
            body: {},
            loading: true,
            success: [(response) => {
                //cancellation_fee
                //fee_explanation
                return dispatch(AppOperations.showAlert({
                    title: strings.alert_message_cancel_ride_title_text,
                    content: response.fee_explanation,
                    buttons: [
                        {text: strings.alert_message_cancel_ride_cancel_text},
                        {
                            text: strings.alert_message_cancel_ride_confirm_text,
                            actions: [types.MIDDLEMAN_INVALIDATE_REQUEST, types.MIDDLEMAN_RIDE_CANCEL]
                        }
                    ]
                }));
            }],
            error: [AppOperations.showAlert],
        }));
    };
};

const doSendLogToBackend = (crashObject: { message: string }, logPrefix?: string = "log") => {
    return thunkBackendAPICall({
        url: `${logPrefix}.logger`,
        body: {lg: [crashObject]},
    });
};

const doGetDriverPhone = () => {
    return thunkBackendAPICall({
        url: "v1/ride/phone",
        body: {},
        success: [CurrentRideOperations.setDriverPhone, CurrentRideOperations.successGettingDriverPhone],
        error: [CurrentRideOperations.failGettingDrivePhone],
    });
};

export default {
    doDecryptToken,
    doGetServiceConfigurations,
    doLastRideStatus,
    doStartupLastRideStatus,
    doProposalStatus,
    doGetPolygonStops,
    doGetPolygonPickupStops,
    doGetPolygonDropoffStops,
    doRideRequest,
    doAcceptRideProposal,
    doGetWalkingMap,
    doCancelRide,
    doGetCancellationFee,
    doSendLogToBackend,
    doGetDriverPhone,
};



// WEBPACK FOOTER //
// ./src/Store/Middleware/Backend/operations.js